/**
 * Pantalla de Auditoría — Fase 8
 *
 * Listado de eventos de auditoría con filtros (acción, entidad, rango de
 * fechas) y detalle expandible con diff visual antes/después.
 *
 * Solo admin_general (RLS de la BD lo refuerza).
 */

import { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
  type GridPaginationModel,
} from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';

import { useAuditoria } from '../../hooks/useAuditoria';
import { formatDateTime } from '../../utils/format';
import {
  type AuditLog,
  type AuditFiltros,
  getAccionLabel,
  getAccionColor,
  getEntidadLabel,
} from '../../types/auditoria';

// ── Opciones de filtros ──────────────────────────────────────────────────────

const ACCIONES_OPCIONES = [
  '', 'INSERT', 'UPDATE', 'DELETE',
  'LOGIN', 'LOGOUT',
  'IMPERSONATE_START', 'IMPERSONATE_STOP',
  'WEBHOOK_RECEIVED', 'PASSWORD_CHANGED',
];

const ENTIDADES_OPCIONES = [
  '', 'empresas', 'afiliados', 'relaciones_laborales',
  'planillas_ddjj', 'cupones', 'pagos',
  'conceptos_aporte', 'parametros', 'usuarios',
  'transacciones_recaudacion',
];

// ── Componente ───────────────────────────────────────────────────────────────

export default function AuditoriaList() {
  const [filtros, setFiltros] = useState<AuditFiltros>({ page: 0, pageSize: 50 });
  const [detalle, setDetalle] = useState<AuditLog | null>(null);

  const { data, isLoading, isError, error } = useAuditoria(filtros);

  const handlePaginationChange = (model: GridPaginationModel) => {
    setFiltros((f) => ({ ...f, page: model.page, pageSize: model.pageSize }));
  };

  const columns: GridColDef<AuditLog>[] = [
    {
      field: 'created_at',
      headerName: 'Fecha y hora',
      width: 160,
      renderCell: (p: GridRenderCellParams<AuditLog>) => (
        <Typography variant="caption">{formatDateTime(p.row.created_at)}</Typography>
      ),
    },
    {
      field: 'usuario',
      headerName: 'Usuario',
      width: 200,
      renderCell: (p: GridRenderCellParams<AuditLog>) =>
        p.row.usuario
          ? `${p.row.usuario.nombre} ${p.row.usuario.apellido ?? ''}`.trim()
          : p.row.usuario_id
            ? '(usuario eliminado)'
            : '—',
    },
    {
      field: 'accion',
      headerName: 'Acción',
      width: 160,
      renderCell: (p: GridRenderCellParams<AuditLog>) => (
        <Chip
          label={getAccionLabel(p.row.accion)}
          size="small"
          color={getAccionColor(p.row.accion)}
          variant={['INSERT', 'UPDATE', 'DELETE'].includes(p.row.accion) ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'entidad',
      headerName: 'Entidad',
      width: 160,
      renderCell: (p: GridRenderCellParams<AuditLog>) => getEntidadLabel(p.row.entidad),
    },
    {
      field: 'entidad_id',
      headerName: 'ID afectado',
      width: 110,
      renderCell: (p: GridRenderCellParams<AuditLog>) =>
        p.row.entidad_id ? (
          <Tooltip title={p.row.entidad_id}>
            <Typography variant="caption" fontFamily="monospace">
              {p.row.entidad_id.slice(0, 8)}…
            </Typography>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (p: GridRenderCellParams<AuditLog>) => (
        <Tooltip title="Ver detalle">
          <IconButton size="small" onClick={() => setDetalle(p.row)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <HistoryIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Auditoría
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Registro de operaciones críticas del sistema. Append-only,
            retención mínima 5 años.
          </Typography>
        </Box>
      </Stack>

      {/* Filtros */}
      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Filtros
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            select
            size="small"
            label="Acción"
            value={filtros.accion ?? ''}
            onChange={(e) =>
              setFiltros((f) => ({ ...f, accion: e.target.value || undefined, page: 0 }))
            }
            sx={{ minWidth: 200 }}
          >
            {ACCIONES_OPCIONES.map((a) => (
              <MenuItem key={a || 'all'} value={a}>
                {a ? getAccionLabel(a) : '(todas)'}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Entidad"
            value={filtros.entidad ?? ''}
            onChange={(e) =>
              setFiltros((f) => ({ ...f, entidad: e.target.value || undefined, page: 0 }))
            }
            sx={{ minWidth: 200 }}
          >
            {ENTIDADES_OPCIONES.map((e) => (
              <MenuItem key={e || 'all'} value={e}>
                {e ? getEntidadLabel(e) : '(todas)'}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            type="date"
            size="small"
            label="Desde"
            value={filtros.desde ?? ''}
            onChange={(e) =>
              setFiltros((f) => ({ ...f, desde: e.target.value || undefined, page: 0 }))
            }
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <TextField
            type="date"
            size="small"
            label="Hasta"
            value={filtros.hasta ?? ''}
            onChange={(e) =>
              setFiltros((f) => ({ ...f, hasta: e.target.value || undefined, page: 0 }))
            }
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />

          <Button
            variant="outlined"
            size="small"
            onClick={() => setFiltros({ page: 0, pageSize: filtros.pageSize ?? 50 })}
          >
            Limpiar filtros
          </Button>
        </Stack>
      </Card>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar auditoría: {(error as Error)?.message ?? 'desconocido'}
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={data?.rows ?? []}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          rowCount={data?.count ?? 0}
          paginationMode="server"
          paginationModel={{
            page: filtros.page ?? 0,
            pageSize: filtros.pageSize ?? 50,
          }}
          onPaginationModelChange={handlePaginationChange}
          pageSizeOptions={[25, 50, 100]}
          sx={{ border: 0 }}
          localeText={{
            noRowsLabel: 'Sin eventos para mostrar',
            MuiTablePagination: {
              labelRowsPerPage: 'Filas por página:',
              labelDisplayedRows: ({ from, to, count }) => `${from}–${to} de ${count}`,
            },
          }}
        />
      </Card>

      {/* Detalle */}
      <Dialog
        open={!!detalle}
        onClose={() => setDetalle(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Detalle del evento</DialogTitle>
        <DialogContent>
          {detalle && <DetalleEvento evento={detalle} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetalle(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Sub-componente: detalle con diff ─────────────────────────────────────────

function DetalleEvento({ evento }: { evento: AuditLog }) {
  const cambios = computarDiff(evento.datos_anteriores, evento.datos_nuevos);

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        <Box>
          <Typography variant="caption" color="text.secondary">Fecha</Typography>
          <Typography variant="body2">{formatDateTime(evento.created_at)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Acción</Typography>
          <Box>
            <Chip
              label={getAccionLabel(evento.accion)}
              size="small"
              color={getAccionColor(evento.accion)}
            />
          </Box>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Entidad</Typography>
          <Typography variant="body2">{getEntidadLabel(evento.entidad)}</Typography>
        </Box>
        {evento.entidad_id && (
          <Box>
            <Typography variant="caption" color="text.secondary">ID afectado</Typography>
            <Typography variant="body2" fontFamily="monospace" fontSize={12}>
              {evento.entidad_id}
            </Typography>
          </Box>
        )}
        <Box>
          <Typography variant="caption" color="text.secondary">Usuario</Typography>
          <Typography variant="body2">
            {evento.usuario
              ? `${evento.usuario.nombre} ${evento.usuario.apellido ?? ''}`.trim() +
                ` <${evento.usuario.email}>`
              : '—'}
          </Typography>
        </Box>
      </Stack>

      <Divider />

      {cambios.length === 0 ? (
        <Alert severity="info" sx={{ fontSize: 13 }}>
          {evento.accion === 'INSERT'
            ? 'Alta sin cambios previos.'
            : evento.accion === 'DELETE'
              ? 'Eliminación — ver datos previos abajo.'
              : 'Sin cambios de campos registrados.'}
        </Alert>
      ) : (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Campos modificados ({cambios.length})
          </Typography>
          <Stack spacing={1}>
            {cambios.map((c) => (
              <CambioRow key={c.campo} cambio={c} />
            ))}
          </Stack>
        </Box>
      )}

      {/* Raw JSON expandible — para casos donde el diff no alcanza */}
      <Box>
        <Typography variant="subtitle2" gutterBottom mt={2}>
          Datos completos
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {evento.datos_anteriores && (
            <Card variant="outlined" sx={{ p: 1.5, flex: 1, bgcolor: 'error.50' }}>
              <Typography variant="caption" color="text.secondary">Anterior</Typography>
              <Box
                component="pre"
                sx={{
                  fontSize: 11,
                  fontFamily: 'monospace',
                  m: 0,
                  mt: 0.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                {JSON.stringify(evento.datos_anteriores, null, 2)}
              </Box>
            </Card>
          )}
          {evento.datos_nuevos && (
            <Card variant="outlined" sx={{ p: 1.5, flex: 1, bgcolor: 'success.50' }}>
              <Typography variant="caption" color="text.secondary">Nuevo</Typography>
              <Box
                component="pre"
                sx={{
                  fontSize: 11,
                  fontFamily: 'monospace',
                  m: 0,
                  mt: 0.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                {JSON.stringify(evento.datos_nuevos, null, 2)}
              </Box>
            </Card>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}

// ── Diff helper ──────────────────────────────────────────────────────────────

interface Cambio {
  campo: string;
  antes: unknown;
  despues: unknown;
}

function computarDiff(
  antes: Record<string, unknown> | null,
  despues: Record<string, unknown> | null,
): Cambio[] {
  if (!antes || !despues) return [];
  const cambios: Cambio[] = [];
  const keys = new Set([...Object.keys(antes), ...Object.keys(despues)]);
  // Excluir timestamps que cambian con cada update
  const exclude = new Set(['updated_at', 'created_at']);
  for (const k of keys) {
    if (exclude.has(k)) continue;
    const a = antes[k];
    const d = despues[k];
    if (JSON.stringify(a) !== JSON.stringify(d)) {
      cambios.push({ campo: k, antes: a, despues: d });
    }
  }
  return cambios;
}

function CambioRow({ cambio }: { cambio: Cambio }) {
  const formatValor = (v: unknown): string => {
    if (v === null || v === undefined) return '(vacío)';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };
  return (
    <Card variant="outlined" sx={{ p: 1, fontSize: 13 }}>
      <Typography variant="body2" fontWeight={600} fontFamily="monospace">
        {cambio.campo}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              bgcolor: 'error.50',
              color: 'error.dark',
              px: 1,
              py: 0.5,
              borderRadius: 0.5,
              display: 'inline-block',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
            }}
          >
            {formatValor(cambio.antes)}
          </Typography>
        </Box>
        <Typography variant="caption">→</Typography>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              bgcolor: 'success.50',
              color: 'success.dark',
              px: 1,
              py: 0.5,
              borderRadius: 0.5,
              display: 'inline-block',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
            }}
          >
            {formatValor(cambio.despues)}
          </Typography>
        </Box>
      </Stack>
    </Card>
  );
}
